import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useStaffMe, isMutating } from '../../hooks/useStaffMe';
import { useToast } from '../../components/ToastProvider';
import { FilterSearch } from '../../components/FilterSearch';
import { FilterPills, type FilterPillGroup } from '../../components/FilterPills';
import { InPageTabs } from '../../components/InPageTabs';
import { EmailValidation } from './EmailValidation';
import { EmailJournalExportButton } from '../../components/EmailJournalExportButton';
import { InstantTime, currentTimeZone, formatInstant, useEffectiveTimeZone } from '../../lib/time-format';
import { SourceChip } from '../../components/SourceChip';
import {
  getTelemetryEvents,
  getTelemetryReportCache,
  getTelemetryReport,
  retryTelemetryReport,
  analyseTelemetry,
  type TelemetryEvent,
  type TelemetrySession,
  type SessionStep,
  type ReportCacheRecord,
  type ReportViewResponse,
  type Channel,
  type TelemetryEventsResponse,
} from '../../lib/flow-validator-api';
import { PerformanceTab } from './PerformanceTab';
import { ZoneCell, ZoneFanoutWarning, ZoneFilter } from '../../components/ZoneIndicators';
import { ZoneBoardLegend } from '../../components/ZoneBoardLegend';
import { useZoneReadReport } from '../../components/ZoneContext';
import {
  fanOutZones, failedZoneLabels, throwIfAllZonesFailed, useWorkingZone, zoneByCode,
  type ZoneResult,
} from '../../lib/zones';
import {
  ALL_ZONES, countInZone, matchesZoneFilter, mergeZoneRows, sortNewestFirst, zoneBoardLegend,
} from '../../lib/zone-board';
import { FreshnessStamp } from '../../components/telemetry/FreshnessStamp';
import { PerfSkeleton } from '../../components/telemetry/PerfPrimitives';
import {
  agentClassOf,
  isMachineRow,
  scopeOf,
  isOnePageZeroDwell,
  NO_CHANNEL,
  AGENT_CLASS_KEYS,
  AGENT_CLASS_LABEL,
  SCOPE_KEYS,
  SCOPE_LABEL,
  type AgentClass,
  type Scope,
} from '../../lib/agent-taxonomy';
// #21 — the ONE mapper for staff-API failures. Never render err.message directly:
// it carries the status, the path and the response body.
import { friendlyFromUnknown } from '../../lib/friendly-error';

/**
 * /admin/diags — "Telemetry & Flow Validator".
 *
 * Two page-level tabs (Flow Validator | Email Validation — Email untouched).
 * Inside Flow Validator, a second-level Raw Events | Report Cache sub-tab split
 * (each full-width) per the approved mock (mockup-flow-validator-attribution-tabs):
 *   • Raw Events  — widened attribution (Channel + Referrer + entry marker,
 *                   channel filter pills, full-payload expand row).
 *   • Report Cache — assembled reports with a status-aware badge, stuck-report
 *                   detection + Retry, and a report viewer that renders the SAME
 *                   stored reportJson the subscriber dashboard reads.
 *
 * Role gate: nexa_admin + super_admin + nexa_support (server-side telemetry:read;
 * mirrored here for UX).
 */

type Badge = { tone: 'green' | 'amber' | 'red' | 'grey'; text: string };

function StatusBadge({ tone, text }: Badge) {
  return <span className={`fv-badge fv-badge-${tone}`}>{text}</span>;
}

/** Section-status chip (scorecard / attribution / intelligence). */
function SectionChip({ label, value }: { label: string; value: string }) {
  const tone =
    value === 'complete' ? 'green' : value === 'failed_narrative' ? 'red' : 'amber';
  return (
    <span className={`fv-chip fv-chip-${tone}`} title={`${label}: ${value}`}>
      {label[0].toUpperCase()}
    </span>
  );
}

// Source taxonomy (three values, written into the data).
type SourceKey = 'live' | 'synthetic' | 'beta';
const SOURCE_KEYS: SourceKey[] = ['live', 'synthetic', 'beta'];
function normaliseSource(source: string): SourceKey {
  return source === 'synthetic' ? 'synthetic' : source === 'beta' ? 'beta' : 'live';
}
const SOURCE_TITLE: Record<SourceKey, string> = {
  live: 'Live — real browser snippet pageview',
  synthetic: 'Synthetic — sent X-Nexa-Synthetic (test tooling / curl), not a real visit',
  // ADR-103 open item 6 — "Beta" named WHEN the rows were written, which told a
  // reader nothing about what they are. "Legacy (untagged)" names the property
  // that actually matters: these rows predate source tagging and cannot be
  // classified retroactively.
  beta: 'Legacy (untagged) — pre-launch build/probe traffic, written before source tagging shipped',
};
function SourceBadge({ source }: { source: string }) {
  const key = normaliseSource(source);
  return (
    <span className={`fv-src fv-src-${key}`} title={SOURCE_TITLE[key]}>{key}</span>
  );
}

/**
 * The agent column (ADR-103 D1) — human / llm / crawler / synthetic from the
 * write-time agentClass. Replaces the old red "bot" chip, which collapsed
 * every non-human into one undifferentiated bucket and so could not tell an
 * LLM reading the site from a scraper hammering it.
 *
 * An untagged historical row renders "Legacy (untagged)", not a guess: the
 * User-Agent that would have classified it was read at the edge and discarded,
 * and no amount of downstream processing can recover it.
 */
/**
 * The D9a noise-filter preference — per-admin, persisted in localStorage, the
 * same treatment the system-events toggle already gets. It is a VIEW
 * preference, not account state: it belongs to this browser and this person,
 * never to the server. Reads defensively so a disabled/failed localStorage
 * degrades to the default rather than breaking the page.
 */
const NOISE_FILTER_KEY = 'nexa.fv.hideThinSessions';

function readNoiseFilterPref(): boolean {
  try {
    return localStorage.getItem(NOISE_FILTER_KEY) === '1';
  } catch {
    return false;
  }
}

function writeNoiseFilterPref(on: boolean): void {
  try {
    localStorage.setItem(NOISE_FILTER_KEY, on ? '1' : '0');
  } catch {
    /* private mode / storage disabled — the toggle still works for this view */
  }
}

const AGENT_TITLE: Record<AgentClass, string> = {
  human: 'Human — a person in a browser',
  llm: 'LLM — an AI assistant or model fetching the page',
  // Crawler two-pass (documented, not a bug — T14 apex UAT 23 Jul 2026): a search
  // crawler often beacons TWICE for one page — once fetching the HTML, once when
  // its renderer runs the JS (ADR-103 D3). Those are two real, distinct requests
  // (each with its own eventId), so the same crawler can appear as two rows in a
  // short window; that is honest, and crawler rows are excluded from every human
  // metric regardless.
  crawler: 'Crawler — a search or SEO indexer. May appear twice for one page: the HTML fetch and the JS-render pass are two real requests (ADR-103 D3), not a duplicate.',
  synthetic: 'Synthetic — platform-generated probe traffic, not a real visit',
};

function AgentBadge({ row }: { row: { agentClass: string; source: string } }) {
  const cls = agentClassOf(row);
  if (!cls) {
    return (
      <span
        className="fv-agent fv-agent-legacy"
        title="Legacy (untagged) — written before the agent taxonomy shipped. The User-Agent was read at the edge and discarded, so this row can never be classified."
      >
        legacy
      </span>
    );
  }
  return (
    <span className={`fv-agent fv-agent-${cls}`} title={AGENT_TITLE[cls]}>{cls}</span>
  );
}

// Channel taxonomy (derived server-side, ADR-047 §3). Display label collapses
// organic_search → "organic" (matching the mock); the CSS class keys off the
// full enum value.
const CHANNEL_KEYS: Channel[] = ['social', 'organic_search', 'paid', 'email', 'referral', 'direct'];
const CHANNEL_LABEL: Record<Channel, string> = {
  paid: 'Paid', email: 'Email', social: 'Social',
  organic_search: 'Organic', referral: 'Referral', direct: 'Direct',
};
function ChannelBadge({ channel, sessionInherited }: { channel: Channel; sessionInherited?: boolean }) {
  return (
    <span
      className={`fv-chan fv-chan-${channel}`}
      title={sessionInherited ? "Session channel — inherited from the session's entry pageview" : CHANNEL_LABEL[channel]}
    >
      {CHANNEL_LABEL[channel].toLowerCase()}
    </span>
  );
}

/** The `step` from a journey event's `meta` JSON ('' if absent/unparseable). */
function journeyStep(meta: string): string {
  try {
    const m = JSON.parse(meta || '{}') as { step?: unknown };
    return typeof m.step === 'string' ? m.step : '';
  } catch {
    return '';
  }
}

// Event-type badge. A journey event (signup-funnel breadcrumb — telemetry-uat2
// decision 3) renders a DISTINCT badge carrying its funnel step; dwell/qos get a
// muted type chip. Pageviews show the ChannelBadge instead (they have a real
// acquisition channel; the other types do not).
function EventTypeBadge({ eventType, meta }: { eventType: string; meta: string }) {
  if (eventType === 'journey') {
    const step = journeyStep(meta);
    return (
      <span className="fv-journey" title={`Journey funnel event${step ? ` — ${step}` : ''}${meta ? ` · ${meta}` : ''}`}>
        journey{step ? `: ${step.replace(/_/g, ' ')}` : ''}
      </span>
    );
  }
  return (
    <span className="fv-etype" title={`${eventType} event`}>{eventType}</span>
  );
}

const EVENTS_LEGEND_COLUMNS = [
  { key: 'sessions', label: 'Sessions' },
  { key: 'events', label: 'Events' },
] as const;
const RECORDS_LEGEND_COLUMNS = [
  { key: 'reports', label: 'Reports' },
  { key: 'attention', label: 'Stuck or failed' },
] as const;

/**
 * Interleave each zone's telemetry into one board. Every event and session keeps its zone tag;
 * nothing is summed (each zone's Tinybird workspace is a separate store — ADR-120 Stage 4).
 * `configured` is true when ANY zone has a Tinybird token; `error` is the first degraded-read
 * reason any answering zone reported (drives the panel badge — the legend names the zone).
 */
export function mergeTelemetryResults(
  results: ReadonlyArray<ZoneResult<TelemetryEventsResponse>>,
): { events: TelemetryEvent[]; sessions: TelemetrySession[]; configured: boolean; error: string | null } {
  const events: TelemetryEvent[] = [];
  const sessions: TelemetrySession[] = [];
  let configured = false;
  let error: string | null = null;
  for (const r of results) {
    const resp = r.items[0];
    if (r.error || !resp) continue;
    configured = configured || resp.configured;
    if (resp.error && !error) error = resp.error;
    for (const e of resp.events ?? []) events.push({ ...e, zone: r.zone.code });
    for (const s of resp.sessions ?? []) sessions.push({ ...s, zone: r.zone.code });
  }
  return {
    events: sortNewestFirst(events, (e) => e.timestamp),
    sessions: sortNewestFirst(sessions, (s) => s.lastActivityAt || s.startedAt),
    configured,
    error,
  };
}

/** Zone code → the legend's Status text, for a zone that answered with a degraded read. */
export function telemetryDegraded(
  results: ReadonlyArray<ZoneResult<TelemetryEventsResponse>> | null,
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const r of results ?? []) {
    const resp = r.items[0];
    if (r.error || !resp) continue;
    if (!resp.configured) out[r.zone.code] = 'Tinybird token not provisioned';
    else if (resp.error === 'query_budget_guard') out[r.zone.code] = 'Query budget guard — try later';
    else if (resp.error) out[r.zone.code] = 'Telemetry read degraded';
  }
  return out;
}

/**
 * The navigation-bar zone report for the Flow tab (ADR-123 Decision 1). A child component so it
 * reports only while the Flow tab is mounted — Email Validation reports its own rows.
 */
function FlowZoneReport({ subTab, sessions, records, eventResults, recordResults, filter }: {
  subTab: 'events' | 'cache' | 'perf';
  sessions: TelemetrySession[] | null;
  records: ReportCacheRecord[] | null;
  eventResults: ZoneResult<TelemetryEventsResponse>[] | null;
  recordResults: ZoneResult<ReportCacheRecord>[] | null;
  filter: string;
}) {
  const onCache = subTab === 'cache';
  const failedZones = useMemo(
    () => (onCache ? failedZoneLabels(recordResults ?? []) : failedZoneLabels(eventResults ?? [])),
    [onCache, recordResults, eventResults],
  );
  useZoneReadReport({
    rows: onCache ? records : sessions,
    failedZones,
    filter,
    counted: subTab !== 'perf',
    scope: 'Flow Validator',
  });
  return null;
}

const RENDER_CAP = 50;
const SESSION_RENDER_CAP = 40;
const FETCH_CAP = 500;

// System events (dwell/qos) are hidden by default in BOTH views; the "show
// system events" checkbox reveals them (kickoff decision 1). Dwell still shows
// as a chip on its pageview regardless.
function isSystemEvent(eventType: string): boolean {
  return eventType === 'dwell' || eventType === 'qos';
}

/** Compact duration: `2m 05s`, `48s`, `1h 04m`. */
function formatDuration(totalSeconds: number): string {
  const s = Math.max(0, Math.round(totalSeconds));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rem = s % 60;
  if (m < 60) return `${m}m ${String(rem).padStart(2, '0')}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${String(m % 60).padStart(2, '0')}m`;
}

// Timeline step icon (mock grammar): ⤓ entry · → nav · 👆 interaction ·
// ▣ modal · 🔴 abandon · ○ dwell/qos system row.
function stepIcon(step: SessionStep): string {
  if (step.eventType === 'journey') {
    if (step.journeyStep === 'abandon') return '🔴';
    if (step.journeyStep === 'modal_open' || step.journeyStep === 'modal') return '▣';
    return '👆';
  }
  // A completed submission reads as an outcome, not another click (D10).
  if (step.eventType === 'interaction') return formSubmitInfo(step.meta) ? '✅' : '👆';
  if (isSystemEvent(step.eventType)) return '○';
  return step.isEntry ? '⤓' : '→';
}

/** Path with the query condensed to `?utm…` (mock decision 2). */
function condensedPath(step: SessionStep): { path: string; hasQuery: boolean } {
  try {
    const u = new URL(step.pageUrl);
    return { path: `${u.pathname}${u.hash}`, hasQuery: u.search.length > 1 };
  } catch {
    return { path: step.pagePath || step.pageUrl, hasQuery: false };
  }
}

/** The interaction fingerprint's human label (semantic event or visible text). */
function interactionLabel(meta: string): { badge: string; text: string; auto: boolean } {
  try {
    const fp = JSON.parse(meta || '{}') as { event?: string; text?: string; aria?: string; tag?: string };
    const badge = fp.event || fp.tag || 'click';
    const text = fp.text || fp.aria || '';
    return { badge, text, auto: !fp.event };
  } catch {
    return { badge: 'click', text: '', auto: true };
  }
}

/**
 * A completed form submission (ADR-103 D10).
 *
 * Form submits ride the `interaction` event type with
 * `{tag:'form', signal:'buying_signal', dest:'form', submitted:true, formId?, provider?}`
 * — the snippet records only the FACT of a submission, never a field value.
 * `formId` plus the page is what turns one blended "contact rate" into
 * per-CTA, per-page conversion.
 *
 * Returns null for anything that is not a submitted form, so the ordinary
 * interaction rendering is untouched.
 */
function formSubmitInfo(meta: string): { formId: string; provider: string } | null {
  try {
    const fp = JSON.parse(meta || '{}') as {
      tag?: string; submitted?: boolean; formId?: string; provider?: string;
    };
    if (fp.tag !== 'form' || fp.submitted !== true) return null;
    return { formId: fp.formId ?? '', provider: fp.provider ?? '' };
  } catch {
    return null;
  }
}

type WindowMinutes = 15 | 60 | 1440;
const WINDOWS: { key: WindowMinutes; label: string }[] = [
  { key: 15, label: '15 min' },
  { key: 60, label: '1h' },
  { key: 1440, label: '24h' },
];
function windowText(m: number): string {
  return m === 15 ? 'last 15 min' : m === 1440 ? 'last 24h' : 'last 1h';
}

// Timestamps render via <InstantTime mode="compact"> (the telemetry-uat2 Time
// Standard): the stored UTC instant is shown in the viewer's zone with a short
// zone label (e.g. `07-11 08:57:03 AEST`) and the raw UTC on hover. This
// supersedes the old raw-UTC `formatTs` (which printed the stored digits with
// no zone). The never-blank fallback is preserved by formatInstant.

/** Human "started Xh ago" from an ISO timestamp (for the stuck-row age). */
function agoText(iso: string): string {
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return '';
  const mins = Math.floor((Date.now() - t) / 60_000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 48) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

/** Hostname of a referrer URL (client-side display only). */
function refHost(url: string): string {
  if (!url) return '';
  try { return new URL(url).hostname; } catch { return url; }
}

/** Registrable-site key: the host with a leading `www.` stripped, so apex and
 * www compare equal (decision 2, telemetry-uat2). */
function registrableSite(host: string): string {
  return (host || '').replace(/^www\./, '');
}

/** True when a captured referrer is on the same registrable site as the page
 * (apex ↔ www). The snippet strips *same-origin* referrers at capture, but a
 * cross-origin same-site hop (apex ↔ www) survives — it is still internal
 * navigation, so it displays as "(in-site)" like the stripped case rather than
 * as an inbound referral. */
function isInSiteReferrer(referrer: string, pageHost: string): boolean {
  const rh = refHost(referrer);
  if (!rh || !pageHost) return false;
  return registrableSite(rh) === registrableSite(pageHost);
}

const IconEye = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" />
  </svg>
);
const IconRefresh = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="23 4 23 10 17 10" /><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
  </svg>
);

export function FlowValidatorPage() {
  const meState = useStaffMe();
  const role = meState.status === 'loaded' ? meState.me.role : null;
  const canAnalyse = role ? (isMutating(role) || role === 'support') : false;
  const toast = useToast();

  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab: 'flow' | 'email' = searchParams.get('tab') === 'email' ? 'email' : 'flow';
  const setActiveTab = (k: 'flow' | 'email') => {
    const next = new URLSearchParams(searchParams);
    if (k === 'email') next.set('tab', 'email');
    else next.delete('tab');
    setSearchParams(next, { replace: true });
  };

  // Sub-tab split (Raw Events | Report Cache) — full-width per panel.
  // D4 — the sub-tab is deep-linkable: `?tab=performance` (or `cache`) selects it
  // on a fresh load, and selecting a sub-tab syncs the URL. `tab=email` still
  // drives the TOP tab (activeTab above); the two share one param without
  // colliding because their value sets are disjoint.
  const subTabFromUrl = (p: string | null): 'events' | 'cache' | 'perf' =>
    p === 'performance' ? 'perf' : p === 'cache' ? 'cache' : 'events';
  const [subTab, setSubTabState] = useState<'events' | 'cache' | 'perf'>(
    () => subTabFromUrl(searchParams.get('tab')),
  );
  const setSubTab = (t: 'events' | 'cache' | 'perf') => {
    setSubTabState(t);
    const next = new URLSearchParams(searchParams);
    if (t === 'perf') next.set('tab', 'performance');
    else if (t === 'cache') next.set('tab', 'cache');
    else next.delete('tab');
    setSearchParams(next, { replace: true });
  };

  const [search, setSearch] = useState('');

  // ── Raw Events ──
  const [events, setEvents] = useState<TelemetryEvent[]>([]);
  const [sessions, setSessions] = useState<TelemetrySession[]>([]);
  const [eventsConfigured, setEventsConfigured] = useState(true);
  const [eventsError, setEventsError] = useState<string | null>(null);
  const [eventsLoading, setEventsLoading] = useState(false);
  // S4d — epoch-ms of the last SUCCESSFUL events load; drives the freshness stamp
  // so a manual snapshot is never mistaken for a live feed (no polling — budget).
  const [eventsLoadedAt, setEventsLoadedAt] = useState<number | null>(null);
  const [sourceSel, setSourceSel] = useState<Set<SourceKey>>(new Set(SOURCE_KEYS));
  const [channelSel, setChannelSel] = useState<Set<Channel>>(new Set(CHANNEL_KEYS));
  // ADR-103 D5 — Scope defaults to EXTERNAL only. Platform-generated probe
  // traffic is diagnostic noise for the common case ("what are real visitors
  // doing?"), so it is opt-in rather than mixed in by default.
  const [scopeSel, setScopeSel] = useState<Set<Scope>>(new Set<Scope>(['external']));
  // D1 — all four agent classes on by default: the taxonomy's purpose is to
  // make machine traffic VISIBLE, so hiding any class by default would undo it.
  const [agentSel, setAgentSel] = useState<Set<AgentClass>>(new Set(AGENT_CLASS_KEYS));
  // D9a — the noise filter. Default OFF (per the mock's legend) so the first
  // view is the honest, unfiltered one; the admin opts into the tidier view.
  const [hideThinSessions, setHideThinSessions] = useState(() => readNoiseFilterPref());
  const [windowMinutes, setWindowMinutes] = useState<WindowMinutes>(60);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  // Sessions view (default) | Events (classic flat stream); system-events toggle.
  const [eventsView, setEventsView] = useState<'sessions' | 'events'>('sessions');
  const [showSystemEvents, setShowSystemEvents] = useState(false);
  const [expandedSessions, setExpandedSessions] = useState<Set<string>>(new Set());

  // ADR-098 — "Open in Flow Validator" deep link (?session=<token>). On first
  // load, seed the search box with the token and pre-expand that session so the
  // journey behind a beta lead resolves directly. Consumed once (the token is
  // then just the active search term); the param is left in the URL.
  const deepLinkSession = searchParams.get('session') ?? '';
  const [deepLinkConsumed, setDeepLinkConsumed] = useState(false);
  const [deepLinkSearchSeeded, setDeepLinkSearchSeeded] = useState(false);
  useEffect(() => {
    if (deepLinkSession && !deepLinkSearchSeeded) {
      setSearch(deepLinkSession);
      setDeepLinkSearchSeeded(true);
    }
  }, [deepLinkSession, deepLinkSearchSeeded]);
  // Expansion resolves only once the first sessions payload has arrived: the
  // deep link carries a sessionTOKEN, but expand state is keyed by sessionId,
  // and one token can now span several visits (30-min idle split). Expand every
  // visit belonging to that token so the whole lead journey is visible.
  useEffect(() => {
    if (!deepLinkSession || deepLinkConsumed || sessions.length === 0) return;
    setExpandedSessions(
      new Set(sessions.filter((s) => s.sessionToken === deepLinkSession).map((s) => s.sessionId)),
    );
    setDeepLinkConsumed(true);
  }, [deepLinkSession, deepLinkConsumed, sessions]);

  // ── Report Cache ──
  const [records, setRecords] = useState<ReportCacheRecord[]>([]);
  const [recordsError, setRecordsError] = useState<string | null>(null);
  const [recordsLoading, setRecordsLoading] = useState(false);
  const [retrying, setRetrying] = useState<Set<string>>(new Set());

  // ── Report viewer modal ──
  const [viewer, setViewer] = useState<{ record: ReportCacheRecord } | null>(null);
  const [viewerData, setViewerData] = useState<ReportViewResponse | null>(null);
  const [viewerLoading, setViewerLoading] = useState(false);
  const [viewerError, setViewerError] = useState<string | null>(null);

  // ── Analyse ──
  const [narrative, setNarrative] = useState<string | null>(null);
  const [analyseConfigured, setAnalyseConfigured] = useState(true);
  const [analyseError, setAnalyseError] = useState<string | null>(null);
  const [analyseLoading, setAnalyseLoading] = useState(false);
  const [analyseOpen, setAnalyseOpen] = useState(false);

  // ── Zones — a READ-ONLY OPS BOARD, so ZONE-AGNOSTIC (Jeff, 11 October 2026, confirmed 13
  // October 2026; admin-portal-ui-standards § "Read-only ops boards are zone-agnostic"). Every
  // read fans out to every zone from this browser, each row is tagged with its zone (the Zone
  // column), a per-zone legend is always on screen, and the zone filter DEFAULTS to "All zones"
  // whatever the Working-in zone is — that governs edits, not reads. Each zone's Tinybird
  // workspace is a separate store (ADR-120 Stage 4), so rows are INTERLEAVED and labelled, never
  // summed. Row actions (view / retry / regenerate) go to the row's own zone.
  const [zoneFilter, setZoneFilter] = useState(ALL_ZONES);
  const [eventResults, setEventResults] = useState<ZoneResult<TelemetryEventsResponse>[] | null>(null);
  const [recordResults, setRecordResults] = useState<ZoneResult<ReportCacheRecord>[] | null>(null);
  // Analyse is an action, not a read: it runs against ONE zone — the narrowed filter's zone, else
  // the Working-in zone — and the panel says which.
  const workingZone = useWorkingZone();
  const analyseZone = zoneFilter === ALL_ZONES ? workingZone : (zoneByCode(zoneFilter) ?? workingZone);


  const loadEvents = useCallback(async (win: WindowMinutes) => {
    setEventsLoading(true);
    setEventsError(null);
    try {
      // One request per zone, from this browser (ADR-120 Amendment 2) — never a server merge.
      const res = await fanOutZones(async (z) => [await getTelemetryEvents(win, FETCH_CAP, z)]);
      // Kept even when every zone failed, so the legend names each zone "Did not respond".
      setEventResults(res);
      throwIfAllZonesFailed(res);
      const merged = mergeTelemetryResults(res);
      setEvents(merged.events);
      setSessions(merged.sessions);
      setExpanded(new Set());
      setExpandedSessions(new Set());
      setEventsConfigured(merged.configured);
      setEventsError(merged.error);
      setEventsLoadedAt(Date.now());
      const failed = failedZoneLabels(res);
      if (failed.length) {
        toast.push({
          variant: 'error',
          title: 'Some telemetry could not be loaded',
          detail: `${failed.join(' and ')} did not respond — its sessions and events are missing from this list.`,
        });
      }
    } catch (err) {
      setEvents([]);
      setSessions([]);
      setEventsError('unreachable');
      toast.push({ variant: 'error', title: 'Could not load telemetry', detail: friendlyFromUnknown(err) });
    } finally {
      setEventsLoading(false);
    }
  }, [toast]);

  const loadRecords = useCallback(async () => {
    setRecordsLoading(true);
    setRecordsError(null);
    try {
      const res = await fanOutZones(async (z) => (await getTelemetryReportCache(z)).records);
      setRecordResults(res);
      throwIfAllZonesFailed(res);
      setRecords(sortNewestFirst(mergeZoneRows(res), (r) => r.generatedAt));
      const failed = failedZoneLabels(res);
      if (failed.length) {
        toast.push({
          variant: 'error',
          title: 'Some report-cache records could not be loaded',
          detail: `${failed.join(' and ')} did not respond — its reports are missing from this list.`,
        });
      }
    } catch (err) {
      setRecords([]);
      setRecordsError('unreachable');
      toast.push({ variant: 'error', title: 'Could not load the report cache', detail: friendlyFromUnknown(err) });
    } finally {
      setRecordsLoading(false);
    }
  }, [toast]);


  useEffect(() => { void loadEvents(windowMinutes); }, [loadEvents, windowMinutes]);
  useEffect(() => { void loadRecords(); }, [loadRecords]);

  const runAnalyse = async () => {
    setAnalyseLoading(true);
    setAnalyseError(null);
    setAnalyseOpen(true);
    try {
      const r = await analyseTelemetry(search.trim() || undefined, analyseZone);
      setNarrative(r.narrative);
      setAnalyseConfigured(r.configured);
    } catch (err) {
      setAnalyseError(err instanceof Error ? err.message : 'Analyse failed.');
    } finally {
      setAnalyseLoading(false);
    }
  };

  const openReport = async (record: ReportCacheRecord) => {
    setViewer({ record });
    setViewerData(null);
    setViewerError(null);
    setViewerLoading(true);
    try {
      // The record lives in exactly one zone — read it from there.
      const r = await getTelemetryReport(record.domainToken, record.reportMonth, zoneByCode(record.zone));
      setViewerData(r);
    } catch (err) {
      setViewerError(err instanceof Error ? err.message : 'Failed to load report.');
    } finally {
      setViewerLoading(false);
    }
  };

  const retryReport = async (record: ReportCacheRecord, engineVersion?: 2) => {
    const key = `${record.zone ?? ''}|${record.domainToken}|${record.reportMonth}`;
    setRetrying((prev) => new Set(prev).add(key));
    try {
      // A re-queue is a write: it goes to the zone that holds the record, never the filter's.
      // ADR-131 — the v2 option runs Website scoring v2 for this regeneration (about fifteen minutes).
      await retryTelemetryReport(record.domainToken, record.reportMonth, zoneByCode(record.zone), engineVersion);
      toast.push({
        variant: 'success',
        title: engineVersion === 2 ? 'Report re-queued on scoring v2' : 'Report re-queued',
        detail: `${record.hostname || record.domainToken} · ${record.reportMonth} will regenerate ${engineVersion === 2 ? 'in about fifteen minutes (website scoring v2)' : 'shortly'}.`,
      });
      // Let the enqueue land, then refresh so the row flips to generating.
      setTimeout(() => { void loadRecords(); }, 1200);
    } catch (err) {
      toast.push({
        variant: 'error',
        title: 'Could not re-queue the report',
        detail: friendlyFromUnknown(err),
      });
    } finally {
      setRetrying((prev) => { const n = new Set(prev); n.delete(key); return n; });
    }
  };

  if (role && !canAnalyse) {
    return (
      <main className="monitoring-page">
        <h1>Telemetry &amp; Flow Validator</h1>
        <p className="crumb">Admin → COG → Flow Validator</p>
        <div className="error-banner">
          Flow Validator is restricted to Nexa Support, Nexa Admin, and Super Admin roles.
        </div>
      </main>
    );
  }

  const eventsBadge: Badge = !eventsConfigured
    ? { tone: 'grey', text: 'Tinybird token not provisioned' }
    : eventsError === 'query_budget_guard'
      ? { tone: 'amber', text: 'Query budget guard — try later' }
      : eventsError
        ? { tone: 'red', text: 'Telemetry read degraded — retry shortly' }
        : events.length > 0
          ? { tone: 'green', text: 'Snippet firing' }
          : { tone: 'amber', text: 'No events yet' };

  // Zone filter first — everything below the legend reads the narrowed set; the legend does not.
  const zoneEvents = events.filter((e) => matchesZoneFilter(e, zoneFilter));
  const zoneSessions = sessions.filter((s) => matchesZoneFilter(s, zoneFilter));
  const zoneRecords = records.filter((r) => matchesZoneFilter(r, zoneFilter));

  // Client-side filtering. Search matches hostname OR domainToken.
  const q = search.trim().toLowerCase();
  const matches = (...fields: string[]) => !q || fields.some((f) => f.toLowerCase().includes(q));

  // ADR-103 — Scope and Agent narrow the set BEFORE source/channel, so every
  // pill count below reads against the same population the table shows.
  const scopedEvents = zoneEvents
    .filter((e) => matches(e.hostname, e.domainToken))
    .filter((e) => scopeSel.has(scopeOf(e)));
  const scopeCount = (k: Scope) =>
    zoneEvents.filter((e) => matches(e.hostname, e.domainToken) && scopeOf(e) === k).length;
  const scopePills: FilterPillGroup<Scope>[] = SCOPE_KEYS.map((k) => ({
    key: k, label: SCOPE_LABEL[k], count: scopeCount(k),
  }));

  // An untagged legacy row has no class, so it can never satisfy an agent
  // filter — it is reachable via the Source row's "Legacy (untagged)" pill,
  // which is the honest place for it.
  const agentCount = (k: AgentClass) => scopedEvents.filter((e) => agentClassOf(e) === k).length;
  const agentPills: FilterPillGroup<AgentClass>[] = AGENT_CLASS_KEYS.map((k) => ({
    key: k, label: AGENT_CLASS_LABEL[k], count: agentCount(k),
  }));
  const agentFilteredEvents = scopedEvents.filter((e) => {
    const c = agentClassOf(e);
    return c === null ? true : agentSel.has(c);
  });

  const searchedEvents = agentFilteredEvents;
  const sourceCount = (k: SourceKey) => searchedEvents.filter((e) => normaliseSource(e.source) === k).length;
  const sourcePills: FilterPillGroup<SourceKey>[] = [
    { key: 'live', label: 'Live', count: sourceCount('live') },
    { key: 'synthetic', label: 'Synthetic', count: sourceCount('synthetic') },
    { key: 'beta', label: 'Legacy (untagged)', count: sourceCount('beta') },
  ];
  const sourceFiltered = searchedEvents.filter((e) => sourceSel.has(normaliseSource(e.source)));
  const channelCount = (k: Channel) => sourceFiltered.filter((e) => e.channel === k).length;
  const channelPills: FilterPillGroup<Channel>[] = CHANNEL_KEYS.map((k) => ({
    key: k, label: CHANNEL_LABEL[k], count: channelCount(k),
  }));
  const filteredEvents = sourceFiltered.filter((e) => channelSel.has(e.channel));
  // System events (dwell/qos) are hidden by default in the Events view too.
  const shownEvents = filteredEvents.filter((e) => showSystemEvents || !isSystemEvent(e.eventType));
  const visibleEvents = shownEvents.slice(0, RENDER_CAP);
  const truncated = shownEvents.length > RENDER_CAP;
  // The cap is per zone (each zone's read is bounded on its own).
  const fetchCapped = (eventResults ?? []).some((r) => (r.items[0]?.events.length ?? 0) >= FETCH_CAP);

  // Sessions view — same search/source/channel filters applied at the session
  // level. sessionToken is included in the match so an ADR-098 "Open in Flow
  // Validator" deep link (?session=<token>) resolves to the one session.
  const filteredSessions = zoneSessions.filter((s) =>
    matches(s.hostname, s.domainToken, s.sessionToken) &&
    scopeSel.has(scopeOf(s)) &&
    (agentClassOf(s) === null || agentSel.has(agentClassOf(s)!)) &&
    sourceSel.has(normaliseSource(s.source)) &&
    // A machine row has no channel, so a channel filter must not silently
    // exclude it — that would make "show me the crawlers" impossible while
    // any channel pill was deselected.
    (isMachineRow(s) || channelSel.has(s.channel)),
  );

  // ── D9a noise filter — applied LAST, and ONLY to what is rendered ────────
  // `filteredSessions` is what every count above is derived from. The noise
  // filter produces a SEPARATE list for display, so toggling it provably
  // cannot move a pill count, a total, or anything a pipe reads. The count
  // line below deliberately reports the unfiltered total plus how many rows
  // are hidden, rather than quietly showing a smaller number.
  const displaySessions = hideThinSessions
    ? filteredSessions.filter((s) => !isOnePageZeroDwell(s))
    : filteredSessions;
  const hiddenThinCount = filteredSessions.length - displaySessions.length;

  const visibleSessions = displaySessions.slice(0, SESSION_RENDER_CAP);
  const sessionsTruncated = displaySessions.length > SESSION_RENDER_CAP;
  const totalEventCount = filteredEvents.length;

  const filteredRecords = zoneRecords.filter((r) => matches(r.hostname, r.domainToken));


  // Status-aware report-cache badge (decision 5).
  const stuckCount = filteredRecords.filter((r) => r.isStuck).length;
  const failedCount = filteredRecords.filter((r) => r.status === 'failed').length;
  const genCount = filteredRecords.filter((r) => r.status === 'generating' && !r.isStuck).length;
  const completeCount = filteredRecords.filter((r) => r.status === 'complete').length;
  const recordsBadge: Badge = recordsError
    ? { tone: 'red', text: 'Load error' }
    : filteredRecords.length === 0
      ? { tone: 'amber', text: 'No reports yet' }
      : failedCount > 0
        ? { tone: 'red', text: [failedCount && `${failedCount} failed`, stuckCount && `${stuckCount} stuck`, completeCount && `${completeCount} complete`].filter(Boolean).join(' · ') }
        : stuckCount > 0 || genCount > 0
          ? { tone: 'amber', text: [stuckCount && `${stuckCount} stuck`, genCount && `${genCount} generating`, completeCount && `${completeCount} complete`].filter(Boolean).join(' · ') }
          : { tone: 'green', text: `Reports current · ${completeCount} complete` };

  // Per-zone legends — over the UNFILTERED rows, so they read the same whatever the filter shows.
  const eventsLegend = zoneBoardLegend(eventResults, {
    degraded: telemetryDegraded(eventResults),
    counts: (code) => ({ sessions: countInZone(sessions, code), events: countInZone(events, code) }),
  });
  const recordsLegend = zoneBoardLegend(recordResults, {
    counts: (code) => ({
      reports: countInZone(records, code),
      attention: countInZone(records, code, (r) => r.isStuck || r.status === 'failed'),
    }),
  });

  const toggleExpanded = (i: number) =>
    setExpanded((prev) => { const n = new Set(prev); n.has(i) ? n.delete(i) : n.add(i); return n; });
  const toggleSession = (token: string) =>
    setExpandedSessions((prev) => { const n = new Set(prev); n.has(token) ? n.delete(token) : n.add(token); return n; });

  return (
    <main className="monitoring-page">
      <h1>Telemetry &amp; Flow Validator</h1>
      <p className="crumb">Admin → COG → Diagnostics</p>

      <InPageTabs
        tabs={[
          { key: 'flow', label: 'Flow Validator' },
          { key: 'email', label: 'Email Validation' },
        ]}
        active={activeTab}
        onChange={setActiveTab}
        ariaLabel="Diagnostics sections"
      />

      {activeTab === 'email' && (
        <EmailValidation
          canRead={canAnalyse}
          renderExport={(rows) => <EmailJournalExportButton rows={rows} />}
        />
      )}

      {activeTab === 'flow' && (
      <>
      <p className="muted" style={{ marginTop: -2, marginBottom: 16, fontSize: 15 }}>
        Verify the snippet → Tinybird → Report Cache pipeline.
      </p>

      {/* Zone-agnostic read-only board: the filter narrows what is SHOWN and defaults to
          "All zones"; it never targets a write. Scoped to the Flow tab — Email Validation has
          its own filter and legend. */}
      <FlowZoneReport
        subTab={subTab}
        sessions={eventsLoading && !eventResults ? null : sessions}
        records={recordsLoading && !recordResults ? null : records}
        eventResults={eventResults}
        recordResults={recordResults}
        filter={zoneFilter}
      />

      <div className="portal-applications-controls fv-controls">
        <FilterSearch
          value={search}
          onChange={setSearch}
          placeholder="Search hostname or domain token…"
          ariaLabel="Search events and report cache by hostname or domain token"
        />
        <ZoneFilter value={zoneFilter} onChange={setZoneFilter} className="zone-board-filter" />
      </div>

      {/* Raw Events | Report Cache sub-strip. */}
      <div className="fv-subtabs" role="tablist" aria-label="Flow validator views">
        <button
          type="button"
          role="tab"
          aria-selected={subTab === 'events'}
          className={`fv-subtab${subTab === 'events' ? ' is-active' : ''}`}
          onClick={() => setSubTab('events')}
        >
          Raw Events <span className="fv-count">{filteredEvents.length}</span>
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={subTab === 'cache'}
          className={`fv-subtab${subTab === 'cache' ? ' is-active' : ''}`}
          onClick={() => setSubTab('cache')}
        >
          Report Cache <span className="fv-count">{filteredRecords.length}</span>
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={subTab === 'perf'}
          className={`fv-subtab${subTab === 'perf' ? ' is-active' : ''}`}
          onClick={() => setSubTab('perf')}
        >
          Performance
        </button>
      </div>

      {/* ── Raw Events ── */}
      {subTab === 'events' && (
        <section className="fv-panel">
          <div className="fv-panel-hdr">
            <div className="mon-seclabel" style={{ margin: 0 }}>
              Raw Events{' '}
              <span className="fv-count">{filteredSessions.length} sessions · {totalEventCount} events</span>
            </div>
            <div className="fv-panel-actions">
              <StatusBadge {...eventsBadge} />
              <FreshnessStamp at={eventsLoadedAt} />
              <button
                type="button"
                className="mon-btn mon-btn-sm"
                onClick={() => void loadEvents(windowMinutes)}
                disabled={eventsLoading}
              >
                {eventsLoading ? 'Loading…' : 'Refresh'}
              </button>
            </div>
          </div>
          <ZoneBoardLegend
            rows={eventsLegend}
            columns={EVENTS_LEGEND_COLUMNS}
            testId="fv-events-zone-legend"
            ariaLabel="Sessions and events by zone"
            loading={eventsLoading && !eventResults}
          />
          <ZoneFanoutWarning
            failedZones={eventResults ? failedZoneLabels(eventResults) : []}
            noun="sessions and events"
            onRetry={() => void loadEvents(windowMinutes)}
          />
          {eventsLoading && !eventResults && (
            <PerfSkeleton label="Loading sessions and events from every zone…" rows={3} />
          )}
          {/* View toggle (Sessions default | Events) + system-events reveal. */}
          <div className="fv-subhdr">
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <div className="fv-viewtoggle" role="tablist" aria-label="Raw events view">
                <button
                  type="button"
                  role="tab"
                  aria-selected={eventsView === 'sessions'}
                  className={eventsView === 'sessions' ? 'is-active' : ''}
                  onClick={() => setEventsView('sessions')}
                >
                  Sessions
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={eventsView === 'events'}
                  className={eventsView === 'events' ? 'is-active' : ''}
                  onClick={() => setEventsView('events')}
                >
                  Events
                </button>
              </div>
            </div>
          </div>
          {/* ADR-103 D9a — filter rows on the LEFT, view options on the RIGHT.
              The toggles moved out of the header into the free right-hand
              space: at top-left they were repeatedly missed in UAT. */}
          <div className="fv-filters-layout">
            <div>
              <div className="fv-subhdr">
                <FilterPills
                  groups={scopePills}
                  selected={scopeSel}
                  onChange={setScopeSel}
                  ariaLabel="Filter events by scope"
                />
                <div className="fv-srcfilter" role="group" aria-label="Time window">
                  {WINDOWS.map((w) => (
                    <button
                      key={w.key}
                      type="button"
                      className={`fv-srcfilter-btn${windowMinutes === w.key ? ' is-active' : ''}`}
                      onClick={() => setWindowMinutes(w.key)}
                      disabled={eventsLoading}
                    >
                      {w.label}
                    </button>
                  ))}
                </div>
              </div>
              <p className="fv-scope-note">
                internal = platform-generated only (probes &amp; tests) — staff UAT is external
              </p>
              <div style={{ margin: '4px 0' }}>
                <FilterPills
                  groups={agentPills}
                  selected={agentSel}
                  onChange={setAgentSel}
                  ariaLabel="Filter events by agent class"
                />
              </div>
              <div style={{ margin: '4px 0' }}>
                <FilterPills
                  groups={sourcePills}
                  selected={sourceSel}
                  onChange={setSourceSel}
                  ariaLabel="Filter events by source"
                />
              </div>
              <div style={{ margin: '4px 0 10px' }}>
                <FilterPills
                  groups={channelPills}
                  selected={channelSel}
                  onChange={setChannelSel}
                  ariaLabel="Filter events by channel"
                />
              </div>
            </div>

            <div className="fv-viewopts">
              <div className="fv-viewopts-t">View options</div>
              <label className="fv-viewopt">
                <input
                  type="checkbox"
                  checked={showSystemEvents}
                  onChange={(ev) => setShowSystemEvents(ev.target.checked)}
                />
                <span>
                  Show system events
                  <i>dwell · qos</i>
                </span>
              </label>
              <label className="fv-viewopt">
                <input
                  type="checkbox"
                  checked={hideThinSessions}
                  onChange={(ev) => {
                    setHideThinSessions(ev.target.checked);
                    writeNoiseFilterPref(ev.target.checked);
                  }}
                />
                <span>
                  Hide 1-page · 0s sessions
                  {/* Display-only, and the label says so: these rows are real
                      (mostly machine reads, legitimately one page and zero
                      seconds) and stay in every count. */}
                  <i>noise filter — any source or agent; does not change counts</i>
                </span>
              </label>
            </div>
          </div>
          <p className="muted-sm fv-trunc" style={{ margin: '0 0 6px' }}>
            {eventsView === 'sessions' ? (
              <>
                nexanomics_events · {filteredSessions.length} session{filteredSessions.length === 1 ? '' : 's'} in the {windowText(windowMinutes)} · a session groups one visitor&apos;s ordered path · dwell shows on its page, not as rows · full URLs on hover
                {/* The TOTAL stays honest and the hidden rows are named. A
                    noise filter that silently shrank this number would read as
                    "there is less traffic", which is exactly what it is not. */}
                {hiddenThinCount > 0 && (
                  <span className="fv-trunc-note"> · {hiddenThinCount} 1-page · 0s session{hiddenThinCount === 1 ? '' : 's'} hidden by the noise filter (still counted)</span>
                )}
                {sessionsTruncated && <span className="fv-trunc-note"> — capped at {SESSION_RENDER_CAP} sessions</span>}
              </>
            ) : (
              <>
                nexanomics_events · showing newest {visibleEvents.length} of {shownEvents.length} ({windowText(windowMinutes)}) · <span style={{ color: '#67e8f9' }}>entry</span> = first pageview of a session (the landing page)
                {truncated && <span className="fv-trunc-note"> — capped at {RENDER_CAP}</span>}
              </>
            )}
            {fetchCapped && <span className="fv-trunc-note"> · window hit the {FETCH_CAP}-row fetch cap (Beta bound)</span>}
          </p>

          {/* ── Sessions view (default) ── */}
          {eventsView === 'sessions' && (
            <div className="fv-sessions">
              {visibleSessions.length === 0 && (
                <p className="muted" style={{ padding: '8px 2px' }}>
                  {eventsLoading
                    ? 'Loading…'
                    : zoneSessions.length === 0
                      ? `No sessions in the ${windowText(windowMinutes)}. Try a wider window.`
                      : 'No sessions match the current search / source / channel filter.'}
                </p>
              )}
              {visibleSessions.map((s) => (
                <SessionRow
                  // sessionId, NOT sessionToken: one tab's token can split into
                  // several visits, so the token is no longer a unique key.
                  key={`${s.zone ?? ''}:${s.sessionId}`}
                  session={s}
                  open={expandedSessions.has(s.sessionId)}
                  showSystemEvents={showSystemEvents}
                  onToggle={() => toggleSession(s.sessionId)}
                />
              ))}
            </div>
          )}

          {/* ── Events view (classic flat stream) ── */}
          {eventsView === 'events' && (
          <table className="mon-vtable">
            <thead>
              <tr>
                <th>Timestamp</th>
                <th>Zone</th>
                <th>Domain</th>
                <th>Source</th>
                <th>Channel</th>
                <th>Referrer</th>
                <th>Page</th>
              </tr>
            </thead>
            <tbody>
              {visibleEvents.length === 0 && (
                <tr>
                  <td colSpan={7} className="muted">
                    {eventsLoading
                      ? 'Loading…'
                      : zoneEvents.length === 0
                        ? `No events in the ${windowText(windowMinutes)}. Try a wider window.`
                        : 'No events match the current search / source / channel filter.'}
                  </td>
                </tr>
              )}
              {visibleEvents.map((e, i) => {
                const host = e.hostname || 'unknown';
                const isOpen = expanded.has(i);
                const geo = e.geoCountry ? `${e.geoCountry}${e.geoRegion ? ` · ${e.geoRegion}` : ''}` : '—';
                return (
                  <Fragment key={`${e.timestamp}-${i}`}>
                    <tr className="fv-row" onClick={() => toggleExpanded(i)} title="Click for full attribution detail">
                      <td className="mon-mono fv-when"><InstantTime iso={e.timestamp} mode="compact" /></td>
                      <td className="fv-zone" data-testid="fv-zone-cell"><ZoneCell zone={e.zone} /></td>
                      <td>
                        <span className={`fv-host${e.hostname ? '' : ' fv-host-unknown'}`}>{host}</span>
                        <span className="fv-token mon-mono" title={e.domainToken}>{e.domainToken}</span>
                      </td>
                      <td>
                        <SourceBadge source={e.source} />
                        {e.isBot ? <span className="fv-bot">bot</span> : null}
                      </td>
                      <td>
                        {e.eventType && e.eventType !== 'pageview'
                          ? <EventTypeBadge eventType={e.eventType} meta={e.meta} />
                          : <ChannelBadge channel={e.channel} sessionInherited={!e.isEntry} />}
                      </td>
                      <td
                        className={`fv-referrer${e.referrer && !isInSiteReferrer(e.referrer, e.hostname) ? '' : ' muted'}`}
                        title={
                          e.referrer
                            ? isInSiteReferrer(e.referrer, e.hostname)
                              ? `Same-site referrer (${refHost(e.referrer)}) — in-site navigation (apex ↔ www)`
                              : e.referrer
                            : e.isEntry
                              ? ''
                              : 'Same-origin referrer stripped — in-site navigation'
                        }
                      >
                        {e.referrer
                          ? isInSiteReferrer(e.referrer, e.hostname)
                            ? '(in-site)'
                            : refHost(e.referrer)
                          : e.isEntry
                            ? '—'
                            : '(in-site)'}
                      </td>
                      <td className="fv-url" title={e.pageUrl}>
                        {e.pageUrl}
                        {e.isEntry && <span className="fv-entry">entry</span>}
                      </td>
                    </tr>
                    {isOpen && (
                      <tr className="fv-row-detail">
                        <td colSpan={7}>
                          <span className="fv-detail-item">Referrer: <strong>{e.referrer || '—'}</strong></span>
                          <span className="fv-detail-item">UTM source: <strong>{e.utmSource || '—'}</strong></span>
                          <span className="fv-detail-item">UTM medium: <strong>{e.utmMedium || '—'}</strong></span>
                          <span className="fv-detail-item">UTM campaign: <strong>{e.utmCampaign || '—'}</strong></span>
                          <span className="fv-detail-item">Device: <strong>{e.deviceType || '—'}</strong></span>
                          <span className="fv-detail-item">Geo: <strong>{geo}</strong></span>
                          <span className="fv-detail-item">Bot: <strong>{e.isBot ? 'yes' : 'no'}</strong></span>
                          <span className="fv-detail-item">Page title: <strong>{e.pageTitle || '—'}</strong></span>
                          <span className="fv-detail-item">Type: <strong>{e.eventType || 'pageview'}</strong></span>
                          {e.meta && <span className="fv-detail-item">Meta: <strong>{e.meta}</strong></span>}
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
          )}
        </section>
      )}

      {/* ── Report Cache ── */}
      {subTab === 'cache' && (
        <section className="fv-panel">
          <div className="fv-panel-hdr">
            <div className="mon-seclabel" style={{ margin: 0 }}>
              Report Cache <span className="fv-count">{filteredRecords.length}</span>
            </div>
            <div className="fv-panel-actions">
              <StatusBadge {...recordsBadge} />
              <button
                type="button"
                className="mon-btn mon-btn-sm"
                onClick={() => void loadRecords()}
                disabled={recordsLoading}
              >
                {recordsLoading ? 'Loading…' : 'Refresh'}
              </button>
            </div>
          </div>

          <div className="fv-explain">
            <strong>What this proves:</strong> every row is one domain's assembled monthly Scorecard report —
            the end product of the pipeline (grading engine + discovery/reputation providers + Tinybird
            attribution + AI narrative), written once and cache-read by the subscriber's CJI dashboard.
            A <strong>complete</strong> row means that domain's report generated end-to-end for that month.
            A row stuck at <strong>generating</strong> means the pipeline died mid-run — the subscriber's
            Scorecard shows "preparing" forever until it is retried.
          </div>
          <div className="fv-legend">
            <span>Sections:</span>
            <span><span className="fv-chip fv-chip-green">S</span> Scorecard — site grading (day-1, no telemetry needed)</span>
            <span><span className="fv-chip fv-chip-amber">A</span> Attribution — channel/referrer data (amber = still collecting telemetry)</span>
            <span><span className="fv-chip fv-chip-amber">I</span> Intelligence — AI narrative (amber = needs one full month of telemetry)</span>
          </div>

          <ZoneBoardLegend
            rows={recordsLegend}
            columns={RECORDS_LEGEND_COLUMNS}
            testId="fv-cache-zone-legend"
            ariaLabel="Report-cache records by zone"
            loading={recordsLoading && !recordResults}
          />
          <ZoneFanoutWarning
            failedZones={recordResults ? failedZoneLabels(recordResults) : []}
            noun="report-cache records"
            onRetry={() => void loadRecords()}
          />
          {recordsLoading && !recordResults && (
            <PerfSkeleton label="Loading the report cache from every zone…" rows={2} />
          )}
          <p className="muted-sm">Nexa-ReportCache · newest 20 records per zone</p>
          <table className="mon-vtable">
            <thead>
              <tr>
                <th>Zone</th>
                <th>Domain</th>
                <th>Month</th>
                <th>Status</th>
                <th>Schema</th>
                <th>Sections</th>
                <th>Generated</th>
                <th className="portal-actions-col">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredRecords.length === 0 && (
                <tr>
                  <td colSpan={8} className="muted">
                    {recordsLoading
                      ? 'Loading…'
                      : zoneRecords.length === 0
                        ? 'No report-cache records.'
                        : 'No report-cache records match the search.'}
                  </td>
                </tr>
              )}
              {filteredRecords.map((r, i) => {
                const key = `${r.zone ?? ''}|${r.domainToken}|${r.reportMonth}`;
                const isRetrying = retrying.has(key);
                return (
                  <tr key={`${r.zone ?? ''}-${r.domainToken}-${r.reportMonth}-${i}`} className={r.isStuck ? 'fv-stuck' : undefined} data-zone={r.zone}>
                    <td className="fv-zone" data-testid="fv-zone-cell"><ZoneCell zone={r.zone} /></td>
                    <td>
                      <span className={`fv-host${r.hostname ? '' : ' fv-host-unknown'}`}>{r.hostname || 'unknown'}</span>
                      <span className="fv-token mon-mono" title={r.domainToken}>{r.domainToken}</span>
                    </td>
                    <td>{r.reportMonth}</td>
                    <td>
                      {r.isStuck ? (
                        <>
                          <span className="fv-status-stuck">generating</span>
                          <span className="fv-age">started {agoText(r.generatedAt)} — stuck</span>
                        </>
                      ) : r.status === 'complete' ? (
                        <span className="fv-status-ok">complete</span>
                      ) : r.status === 'failed' ? (
                        <span className="fv-status-failed">failed</span>
                      ) : (
                        <span>{r.status}</span>
                      )}
                    </td>
                    <td>{r.schemaVersion ?? <span className="muted">—</span>}</td>
                    <td>
                      {r.sections ? (
                        <span className="fv-chips">
                          <SectionChip label="scorecard" value={r.sections.scorecard} />
                          <SectionChip label="attribution" value={r.sections.attribution} />
                          <SectionChip label="intelligence" value={r.sections.intelligence} />
                        </span>
                      ) : (
                        <span className="muted">—</span>
                      )}
                    </td>
                    <td className="mon-mono fv-when"><InstantTime iso={r.generatedAt} mode="compact" /></td>
                    <td className="portal-actions-col">
                      <div className="portal-actions-row">
                        {r.status === 'complete' && (
                          <>
                            <button type="button" className="portal-action-icon-btn" title="View report" aria-label="View report" onClick={() => void openReport(r)}>
                              <IconEye />
                            </button>
                            <button type="button" className="portal-action-icon-btn" title="Regenerate report" aria-label="Regenerate report" disabled={isRetrying} onClick={() => void retryReport(r)}>
                              <IconRefresh />
                            </button>
                            <button type="button" className="portal-action-icon-btn" title="Regenerate with website scoring v2 (ADR-131 — about fifteen minutes)" aria-label="Regenerate with website scoring v2" disabled={isRetrying} onClick={() => void retryReport(r, 2)}>
                              <span aria-hidden="true" style={{ fontSize: 12, fontWeight: 700, letterSpacing: 0.3 }}>v2</span>
                            </button>
                          </>
                        )}
                        {(r.isStuck || r.status === 'failed') && (
                          <button
                            type="button"
                            className="portal-action-icon-btn"
                            title="Retry — re-queue this report now"
                            aria-label="Retry report generation"
                            disabled={isRetrying}
                            style={{ borderColor: 'rgba(251,191,36,0.55)', color: '#fcd34d' }}
                            onClick={() => void retryReport(r)}
                          >
                            <IconRefresh />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </section>
      )}

      {/* ── Performance (QoS baseline, ADR-093) ── */}
      {/* ── Performance (three sections: Telemetry Health · Visitor Performance · Visitor Experience — ADR-110) ── */}
      {/* Fans out to every zone itself; the zone filter narrows what it shows. */}
      {subTab === 'perf' && <PerformanceTab active={subTab === 'perf'} zoneFilter={zoneFilter} />}

      {/* ── Analyse ── */}
      <div className="fv-analyse">
        <button
          type="button"
          className="mon-btn mon-btn-primary"
          onClick={() => void runAnalyse()}
          disabled={analyseLoading}
        >
          {analyseLoading ? 'Analysing…' : 'Analyse flow'}
        </button>
        <span className="muted-sm" data-testid="fv-analyse-zone">
          Asks the model to diagnose what's flowing, what's missing, and any attribution gaps —
          in <strong>{analyseZone.label}</strong>
          {zoneFilter === ALL_ZONES ? ' (your Working-in zone; narrow the Zone filter to analyse another)' : ''}.
        </span>
      </div>

      {analyseOpen && (
        <div className="fv-narrative">
          {analyseLoading && <p className="muted">Analysing the current snapshot…</p>}
          {!analyseLoading && analyseError && (
            <div className="error-banner">Analyse failed: {analyseError}</div>
          )}
          {!analyseLoading && !analyseError && !analyseConfigured && (
            <p className="muted">Anthropic API key is not yet provisioned in Secrets Manager.</p>
          )}
          {!analyseLoading && !analyseError && analyseConfigured && narrative && (
            <p className="fv-narrative-text">{narrative}</p>
          )}
        </div>
      )}

      {viewer && (
        <ReportViewer
          record={viewer.record}
          data={viewerData}
          loading={viewerLoading}
          error={viewerError}
          onClose={() => setViewer(null)}
        />
      )}
      </>
      )}
    </main>
  );
}

// ---------------------------------------------------------------------------
// Report viewer modal — renders the SAME stored reportJson the subscriber's CJI
// dashboard reads: a summary strip up top + raw JSON below (decision 7).
// ---------------------------------------------------------------------------
const SCORE_TILES: { key: string; label: string }[] = [
  { key: 'overall', label: 'Overall' },
  { key: 'search', label: 'Search' },
  { key: 'socialMedia', label: 'Social' },
  { key: 'mobile', label: 'Mobile' },
  { key: 'desktop', label: 'Desktop' },
  { key: 'reputation', label: 'Reputation' },
];

function ReportViewer({
  record, data, loading, error, onClose,
}: {
  record: ReportCacheRecord;
  data: ReportViewResponse | null;
  loading: boolean;
  error: string | null;
  onClose: () => void;
}) {
  const title = `Monthly report — ${record.hostname || record.domainToken} · ${record.reportMonth}`;
  const rj = (data?.reportJson ?? null) as Record<string, unknown> | null;
  const scores = (rj?.scores ?? {}) as Record<string, unknown>;
  const grades = (rj?.grades ?? {}) as Record<string, { grade?: string; colour?: string }>;
  const schemaVersion = typeof rj?.schemaVersion === 'number' ? rj.schemaVersion : null;
  const sections = (rj?.sections ?? null) as { scorecard: string; attribution: string; intelligence: string } | null;

  return (
    <div className="fv-modal-overlay" onClick={(ev) => { if (ev.target === ev.currentTarget) onClose(); }}>
      <div className="fv-modal" role="dialog" aria-label={title}>
        <div className="fv-modal-hdr">
          <h2>{title}</h2>
          <button type="button" className="fv-modal-close" onClick={onClose} aria-label="Close">×</button>
        </div>

        {loading && <p className="fv-modal-note">Loading the stored report…</p>}
        {!loading && error && <div className="error-banner">{error}</div>}

        {!loading && !error && data && !rj && (
          <p className="fv-modal-note">
            This report has no assembled <span className="mon-mono">reportJson</span> yet
            (status: <strong>{data.status}</strong>) — it is still generating or failed before completing.
          </p>
        )}

        {!loading && !error && rj && (
          <>
            <p className="fv-modal-note">
              This is the exact stored <span className="mon-mono">reportJson</span> the subscriber's CJI
              dashboard reads — same data, staff rendering. Generated <InstantTime iso={data?.generatedAt ?? ''} mode="compact" />
              {data?.trigger ? ` · trigger: ${data.trigger}` : ''}
              {schemaVersion != null ? ` · schema v${schemaVersion}` : ''}.
            </p>
            <div className="fv-score-row">
              {SCORE_TILES.filter((t) => typeof scores[t.key] === 'number').map((t) => {
                const g = grades[t.key] ?? {};
                return (
                  <div className="fv-score" key={t.key}>
                    <span className="n">{scores[t.key] as number}</span>
                    {g.grade && <span className="g" style={{ color: g.colour ?? undefined }}>{g.grade}</span>}
                    <span className="l">{t.label}</span>
                  </div>
                );
              })}
            </div>
            {sections && (
              <div className="fv-legend" style={{ marginTop: 0 }}>
                <span><span className="fv-chip fv-chip-green">S</span> scorecard: {sections.scorecard}</span>
                <span><span className="fv-chip fv-chip-amber">A</span> attribution: {sections.attribution}</span>
                <span><span className="fv-chip fv-chip-amber">I</span> intelligence: {sections.intelligence}</span>
              </div>
            )}
            <h3>reportJson (raw)</h3>
            <div className="fv-json">{JSON.stringify(rj, null, 2)}</div>
          </>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sessions view (ADR-095) — a session groups one visitor's ordered path. Look
// copied from mockup-flow-validator-sessions-v4.
// ---------------------------------------------------------------------------

/** Step time in the viewer's zone (ADR-094 — routes through formatInstant, never
 *  a raw new Date on the naive ClickHouse instant). Bare HH:MM:SS: the session
 *  header already carries the zone label, so per-row labels would be noise; the
 *  UTC instant is on hover. */
function StepTime({ iso }: { iso: string }) {
  const zone = useEffectiveTimeZone();
  const { text, label, utc } = formatInstant(iso, zone, 'time-sec');
  const bare = label && text.endsWith(` ${label}`) ? text.slice(0, -(label.length + 1)) : text;
  return <span className="fv-step-time" title={utc}>{bare}</span>;
}

function SessionStepRow({ step }: { step: SessionStep }) {
  const dwellChip = step.dwellSeconds > 0 && (step.eventType === 'pageview' || step.eventType === '')
    ? <span className="fv-dwell-chip">dwell {step.dwellSeconds}s</span>
    : <span />;

  let content: JSX.Element;
  const submit = step.eventType === 'interaction' ? formSubmitInfo(step.meta) : null;
  if (submit) {
    // D10 — a completed CTA gets the GREEN treatment: it is the one step in a
    // path that represents a won outcome rather than a movement. The page and
    // form id ride alongside so the row answers "which CTA, on which page?"
    // rather than only "a form was submitted somewhere".
    const { path } = condensedPath(step);
    content = (
      <span>
        <span className="fv-evt fv-evt-submitted">journey: submitted</span>
        <span className="fv-step-label">form submitted — success</span>
        {path && <span className="fv-step-note">· on <span className="fv-path-inline">{path}</span></span>}
        {submit.formId && <span className="fv-step-note">· form: {submit.formId}</span>}
        {submit.provider && <span className="fv-step-note">· via {submit.provider}</span>}
      </span>
    );
  } else if (step.eventType === 'interaction') {
    const { badge, text, auto } = interactionLabel(step.meta);
    content = (
      <span>
        <span className="fv-evt fv-evt-int">{badge}</span>
        {text && <span className="fv-step-label">{text}</span>}
        {auto && <span className="fv-step-note">(auto-captured)</span>}
      </span>
    );
  } else if (step.eventType === 'journey') {
    const abandon = step.journeyStep === 'abandon';
    const modal = step.journeyStep === 'modal_open' || step.journeyStep === 'modal';
    content = (
      <span>
        <span className={`fv-evt${abandon ? ' fv-evt-abandon' : ''}`}>
          journey{step.journeyStep ? `: ${step.journeyStep.replace(/_/g, ' ')}` : ''}
        </span>
        {abandon && <span className="fv-step-label">closed without submitting</span>}
        {modal && <span className="fv-step-label">modal open</span>}
      </span>
    );
  } else {
    // pageview / dwell / qos — a page path row.
    const { path, hasQuery } = condensedPath(step);
    content = (
      <span className="fv-path" title={step.pageUrl}>
        {path}
        {hasQuery && <span className="q"> ?utm…</span>}
        {step.isEntry && <span className="entry-tag">entry</span>}
        {isSystemEvent(step.eventType) && <span className="fv-step-note">{step.eventType}</span>}
      </span>
    );
  }

  return (
    <div className={`fv-step${isSystemEvent(step.eventType) ? ' fv-step-sys' : ''}`}>
      <StepTime iso={step.timestamp} />
      <span className="fv-step-icon">{stepIcon(step)}</span>
      {content}
      {dwellChip}
    </div>
  );
}

// Exported for the measurement harness ONLY (scripts/probe-flow-validator-row.mjs).
// The alignment gate must measure the REAL component with the REAL stylesheet —
// a harness copy of the markup would drift from what ships and prove nothing.
export function SessionRow({ session, open, showSystemEvents, onToggle }: {
  session: TelemetrySession;
  open: boolean;
  showSystemEvents: boolean;
  onToggle: () => void;
}) {
  const host = session.hostname || 'unknown';
  const isSynthetic = session.source === 'synthetic';
  const steps = session.steps.filter((s) => showSystemEvents || !isSystemEvent(s.eventType));
  const stats = [
    `${session.pageCount} page${session.pageCount === 1 ? '' : 's'}`,
    session.interactionCount > 0 ? `${session.interactionCount} interaction${session.interactionCount === 1 ? '' : 's'}` : '',
    formatDuration(session.durationSeconds),
    // D2 — a stitched visit reads honestly (text only, so the D9a fixed-column
    // alignment is untouched — no new chip/box in the row). It names WHAT was
    // stitched: an in-app browser minting a token per page, or simply the
    // visitor's own tabs. Calling ordinary desktop tabs "in-app fragments" was
    // the 25 July 2026 UAT mislabel — `fragmentCount` counts tokens, and a
    // sessionToken is per-tab, so it can never mean "in-app" on its own.
    session.fragmentCount > 1
      ? session.inApp
        ? `stitched · ${session.fragmentCount} in-app fragments`
        : `stitched · ${session.fragmentCount} tabs`
      : '',
  ].filter(Boolean).join(' · ');

  return (
    <div className={`fv-session${isSynthetic ? ' fv-session-synthetic' : ''}`}>
      {/* FIXED COLUMNS (D9a): time · domain · zone · source · agent · identity ·
          channel · activity · caret. One chip type per column — nothing wraps
          into a mixed cluster. The old build packed identity and channel into
          a single flex cell, which is what made the row hard to scan. */}
      <button type="button" className="fv-session-hdr" onClick={onToggle} aria-expanded={open}>
        {/* The visible time is when the visit STARTED; the list is ordered by
            last activity, so the tooltip carries that rather than a new column
            (an extra chip here would change the D9a fixed-column layout and
            need a fresh alignment measurement). */}
        <span
          className="fv-when mon-mono"
          title={session.lastActivityAt ? `Last activity ${formatInstant(session.lastActivityAt, currentTimeZone(), 'datetime-sec').text}` : undefined}
        >
          <InstantTime iso={session.startedAt} mode="compact" />
        </span>
        <span>
          <span className={`fv-host${session.hostname ? '' : ' fv-host-unknown'}`}>{host}</span>
          <span className="fv-token mon-mono" title={session.domainToken}>{session.domainToken}</span>
        </span>
        {/* Zone column (13 October 2026 — read-only ops boards are zone-agnostic). */}
        <span className="fv-zone" data-testid="fv-zone-cell"><ZoneCell zone={session.zone} /></span>
        <SourceBadge source={session.source} />
        <AgentBadge row={session} />
        <SourceChip identity={session.identity} />
        {/* Machine rows show "—": channel describes how a HUMAN arrived, and a
            crawler was not acquired from anywhere. The pre-taxonomy build
            labelled these "Direct", which both misread the row and inflated
            the Direct bucket with bot traffic.

            ONE exception (28 July 2026): a `domain-probe` row spends the slot on
            its DETECTION TIER instead of a dash. Every VISIBLE domain-probe row
            is definitionally an L3 (Playwright render) check — L1 (`healthy-skip`)
            and L2 (`diagnosed-csp-fix`) resolve without a browser and so never
            beacon at all. The tier is therefore derivable from the row's mere
            existence, not guessed. Tiering is a domain-probe-only concept: the
            other synthetic producers (embed-probe, submit-probe, test-beacon)
            and every llm/crawler row keep the plain dash. */}
        {isMachineRow(session)
          ? session.agentName === 'domain-probe'
            ? <span className="fv-chan-tier" title="This domain-probe row is an L3 (Playwright render) check — L1/L2 pre-filter exits resolve without a browser and never produce a visible event">L3 Test</span>
            : <span className="fv-chan-none" title="Channel describes how a human arrived — it is meaningless for a machine read">{NO_CHANNEL}</span>
          : <ChannelBadge channel={session.channel} />}
        <span className="fv-sess-stats">{stats}</span>
        <span className="fv-caret">{open ? '▾' : '▸'}</span>
      </button>
      {open && (
        <div className="fv-timeline">
          {steps.map((step, i) => (
            <SessionStepRow key={`${step.timestamp}-${step.eventType}-${i}`} step={step} />
          ))}
        </div>
      )}
    </div>
  );
}
